import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, BarChart3 } from "lucide-react";
import AgencyScorecard from "@/components/AgencyScorecard";
import ImpegniChiusura from "@/components/ImpegniChiusura";
import AgencyIdentityBox from "@/components/AgencyIdentityBox";
import AgencyAnagrafica from "@/components/AgencyAnagrafica";
import AgencyContratto from "@/components/AgencyContratto";
import AgencyCondizioni from "@/components/AgencyCondizioni";

const STATO_STYLE = {
  "In linea": "bg-green-100 text-green-800 border-green-300",
  "Attenzione": "bg-amber-100 text-amber-800 border-amber-300",
  "Fuori presidio": "bg-red-100 text-red-700 border-red-300",
  "N/D": "bg-muted text-muted-foreground border-border",
};

export default function AgencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [anaOpen, setAnaOpen] = useState(false);
  const [condPrefill, setCondPrefill] = useState(null);
  const [condOpenSignal, setCondOpenSignal] = useState(0);

  const canEdit = ["admin", "back_office"].includes(user?.role);

  const load = () => api.get(`/agencies/${id}/portfolio`).then((r) => setData(r.data));
  useEffect(() => { setData(null); setCondPrefill(null); setCondOpenSignal(0); load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/agencies").then((r) => setAgencies((r.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => setAgencies([])); }, []);

  const reload = () => { load(); };
  const onExtracted = (relazione) => { setCondPrefill(relazione); setCondOpenSignal((s) => s + 1); };

  if (!data) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const agency = data.agency;
  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="agency-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/agencies" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-agencies">
          <ArrowLeft className="h-4 w-4" /> Agenzie
        </Link>
        {agencies.length > 1 && (
          <Select value={id} onValueChange={(v) => v !== id && navigate(`/agencies/${v}`)}>
            <SelectTrigger className="h-9 w-[240px]" data-testid="agency-quick-switch"><SelectValue placeholder="Cambia agenzia" /></SelectTrigger>
            <SelectContent className="max-h-[400px]">
              {agencies.map((a) => (<SelectItem key={a.id} value={a.id} data-testid={`agency-switch-${a.id}`}>{a.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
      </div>

      <AgencyIdentityBox agency={agency} summary={s} canEdit={canEdit} onEdit={() => setAnaOpen(true)} />

      <AgencyAnagrafica agency={agency} agencies={agencies} canEdit={canEdit} open={anaOpen} setOpen={setAnaOpen} onSaved={reload} />

      <AgencyContratto agencyId={agency.id} agency={agency} canEdit={canEdit} onExtracted={onExtracted} onChanged={reload} />

      <AgencyCondizioni agencyId={agency.id} relazione={agency.relazione} canEdit={canEdit} prefill={condPrefill} openSignal={condOpenSignal} onSaved={reload} />

      {/* Presidio commerciale (sezione operativa ridotta) */}
      <div className="pt-2" data-testid="agency-presidio-section">
        <h2 className="mb-3 flex items-center gap-2 text-base font-bold tracking-tight text-muted-foreground"><BarChart3 className="h-5 w-5" />Presidio commerciale</h2>

        {user?.role !== "agency" && <AgencyScorecard agencyId={agency.id} />}

        <div className="mt-4"><ImpegniChiusura agencyId={agency.id} canEdit /></div>

        <Card className="mt-4 p-4" data-testid="agency-clients-table">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Clienti assegnati ({data.partners.length})</div>
          <div className="max-h-96 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead><TableHead>Classe</TableHead><TableHead>Provincia</TableHead>
                  <TableHead className="text-right">Potenziale</TableHead><TableHead>Presidio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.partners.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/partners/${p.id}`)} data-testid={`portfolio-row-${p.id}`}>
                    <TableCell className="font-semibold">{p.name}</TableCell>
                    <TableCell>{p.partner_class ? <Badge variant="secondary">{p.partner_class}</Badge> : "—"}</TableCell>
                    <TableCell className="text-sm">{p.province || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{p.potenziale_annuo != null ? eur(p.potenziale_annuo) : "—"}</TableCell>
                    <TableCell><Badge className={`border text-[11px] ${STATO_STYLE[p.stato_presidio]}`}>{p.stato_presidio}</Badge></TableCell>
                  </TableRow>
                ))}
                {data.partners.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Nessun cliente assegnato.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
